import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';

const VideoWrapper = styled.div`
  width: 500px;
  height: 202px;
  overflow: hidden;
  position: relative;
  background: #100b20;

  ${mediaQuery.uptoMobile} {
    width: 336px;
    height: 147px;
  }
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
`;

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(49, 35, 87, 0) 7.77%,
    rgba(49, 35, 87, 0.15) 26.33%,
    rgba(49, 35, 87, 0.35) 39.88%,
    rgba(49, 35, 87, 0.75) 67.13%,
    rgba(49, 35, 87, 0.9) 79.83%,
    #312357 92.23%
  );
`;

const CenterControl = styled.button`
  opacity: 0;

  ${VideoWrapper}:hover & {
    opacity: 1;
  }
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  width: 56px;
  height: 56px;

  ${mediaQuery.uptoMobile} {
    width: 52px;
    height: 52px;
  }

  transform: translate(-50%, -50%);

  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.69);

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  padding: 0;
`;

const PauseIcon = styled.span`
  display: flex;
  gap: 4px;

  &::before,
  &::after {
    content: '';
    width: 5px;
    height: 14px;
    border-radius: 999px;
    background: #ffffff;
  }
`;

const PlayIcon = styled.span`
  width: 0;
  height: 0;

  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 14px solid #ffffff;

  transform: translateX(2px);
`;

type VideoPreviewProps = {
  src: string;
};

export default function VideoPreview({ src }: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);

  const playVideo = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      video.muted = false;
      video.volume = 1;
      await video.play();

      setIsPlaying(true);
    } catch {
      setIsPlaying(false);
    }
  };

  const pauseVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  };

  useEffect(() => {
    playVideo();
  }, []);

  return (
    <VideoWrapper>
      <StyledVideo
        ref={videoRef}
        src={src}
        playsInline
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <GradientOverlay />

      <CenterControl
        type="button"
        onClick={togglePlayPause}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </CenterControl>
    </VideoWrapper>
  );
}
